/**
 * Button — primary interactive button primitive.
 *
 * Variants map to semantic intent; sizes map to the 4/8px spacing scale.
 * Keyboard-accessible by default (native <button>), with a visible
 * focus-visible ring from global.css.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Semantic intent of the button. */
  variant?: ButtonVariant;
  /** Size preset; defaults to medium. */
  size?: ButtonSize;
  /** Show a spinner and disable interaction while an async action runs. */
  loading?: boolean;
  /** Accessible label when the text content is not descriptive enough. */
  'aria-label'?: string;
  children?: ReactNode;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: styles.primary ?? '',
  secondary: styles.secondary ?? '',
  ghost: styles.ghost ?? '',
  danger: styles.danger ?? '',
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: styles.sm ?? '',
  md: styles.md ?? '',
};

/**
 * Render a styled button. Forwards all native button props; collapses
 * `loading` into `disabled` and exposes an accessible busy state.
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    styles.btn ?? '',
    VARIANT_CLASS[variant],
    SIZE_CLASS[size],
    loading ? (styles.loading ?? '') : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className={styles.spinner ?? ''} aria-hidden="true" /> : null}
      <span className={styles.label ?? ''}>{children}</span>
    </button>
  );
}
